/**
 * localTreeDatasource.js
 *
 * Replaces ag-grid's Client-Side Row Model + treeData/getDataPath.
 *
 * Problem it solves: CSRM + treeData builds a RowNode for every leaf AND
 * every group level, for the ENTIRE dataset, immediately, on every rowData
 * change. That's what inflates a ~4.5GB fetched dataset into ~8GB+ browser
 * memory and crashes the tab.
 *
 * Fix: keep the fetch exactly as-is (one call to getGlReportData(), no BE
 * pagination needed). Group the already-fetched flat array into a plain JS
 * tree ONCE (cheap — just object references, no ag-grid involved). Then use
 * ag-grid's Server-Side Row Model in its CLASSIC row-grouping mode (v25-safe
 * — NOT tree data, which requires ag-grid v27.1+). getRows() reads from this
 * local tree instead of a network endpoint. serverSideStoreType: 'partial'
 * means only groups/rows currently expanded/visible get RowNodes created,
 * and they're purged when collapsed — so memory stays bounded regardless of
 * whether the report covers 6 months or 5 years.
 *
 * Classic SSRM row grouping (as opposed to tree data) determines whether a
 * response represents "group rows" purely from request depth
 * (groupKeys.length vs the number of row-group columns) — there's no
 * isServerSideGroup/getServerSideGroupKey involved. Two hidden columns
 * (`reportGroup`, `groupAccount`) drive the two grouping levels; see
 * Columns.js.CHANGES.md.
 *
 * FILTERING: under SSRM, filtering is not automatic — ag-grid sends the
 * active column filters as params.request.filterModel on every getRows()
 * call, and it's the datasource's own job to apply them. This file filters
 * leaf rows directly, hides group branches with zero matching descendants,
 * and shows group totals based on the FILTERED subset so displayed totals
 * stay consistent with what's actually visible.
 *
 * SORTING (PA-2251): same deal, and FLAT mode only — every leaf row is served at
 * the root level and request.sortModel applies across the whole dataset. GROUPED
 * mode ignores sortModel: a partial store fetches each level independently, so a
 * sort there also disturbs the account band order. Its columns are therefore not
 * sortable at all (see buildWorksheetColumnDefs in GlReportCustom.vue).
 *
 * Both modes use the SAME tree, so switching the option never refetches.
 */

/**
 * Groups a flat array of transaction lines into a nested tree, using the
 * exact same path logic your current getDataPath() uses:
 *   [type-label] -> [grandparent account] -> [parent account] -> [code]
 *
 * Also rolls up `amount` totals from leaves to root ONCE here, replacing
 * ag-grid's live aggFunc: 'sum' (which SSRM can't compute across rows it
 * hasn't loaded).
 *
 * @param {Array<Object>} flatRows - e.g. this.workSheetData
 * @returns {Object} root tree node: { children: Map, rows: [], amount }
 */
export function buildLocalTree (flatRows, isAfterSave = false) {
  const root = { key: null, children: new Map(), rows: [], amount: 0 }

  for (const row of flatRows) {
    const path = []
    if (row.type) {
      path.push(row.type.toLowerCase() === 'grand_total' ? 'GRAND TOTAL' : 'UNKNOWN EXPENSES')
    }
    if (row.grandparent) path.push(row.grandparent)
    if (row.parent) path.push(row.parent)
    if (row.code) path.push(row.code)

    let node = root
    for (const key of path) {
      if (!node.children.has(key)) {
        node.children.set(key, { key, children: new Map(), rows: [], amount: 0, parent: node })
      }
      node = node.children.get(key)
    }
    // Leaf row lives at the final path segment's node
    node.rows.push(row)
  }

  if (isAfterSave) sortTree(root)
  rollUpAmounts(root)
  return root
}

// Matches buildWorkSheetReport's collator so tree and report order agree.
const ORDER_COLLATOR = new Intl.Collator('en', { numeric: true })

function leafTransNumber (node) {
  return node.rows.length ? String(node.rows[0].trans_number || '') : ''
}

/*
 * Account groups ascending by code, transactions ascending by trans_number.
 * Needed once a row is re-coded to another account: it keeps its original
 * position in the flat array, so insertion order no longer reflects the report's.
 * Leaves sort on trans_number, not on their map key — the key is
 * "<index>__<trans_number>" and is re-keyed on line_id when a row moves.
 */
function sortTree (node) {
  if (node.children.size > 1) {
    const entries = [...node.children.entries()]
    entries.sort((a, b) => {
      const aIsLeaf = a[1].children.size === 0
      const bIsLeaf = b[1].children.size === 0
      // Groups before leaves keeps the comparator a consistent total order.
      if (aIsLeaf !== bIsLeaf) return aIsLeaf ? 1 : -1
      if (aIsLeaf) {
        return ORDER_COLLATOR.compare(leafTransNumber(a[1]), leafTransNumber(b[1]))
      }
      return ORDER_COLLATOR.compare(String(a[0]), String(b[0]))
    })
    node.children = new Map(entries)
  }
  if (node.rows.length > 1) {
    node.rows.sort((a, b) => ORDER_COLLATOR.compare(String(a.trans_number || ''), String(b.trans_number || '')))
  }
  for (const child of node.children.values()) {
    sortTree(child)
  }
}

// Sums leaf amounts up through every ancestor, once, top-down recursion /
// bottom-up accumulation. O(n) over the flat row count.
function rollUpAmounts (node) {
  let total = 0
  for (const row of node.rows) {
    total += parseFloat(row.amount) || 0
  }
  for (const child of node.children.values()) {
    total += rollUpAmounts(child)
  }
  node.amount = total
  return total
}

/**
 * Checks a single row against one agTextColumnFilter condition
 * (the type ag-grid sends for a text floating filter: { filterType, type, filter }).
 */
function textConditionMatches (value, condition) {
  if (!condition) return true
  const val = (value === null || value === undefined) ? '' : String(value).toLowerCase()
  const filterText = (condition.filter || '').toLowerCase()

  switch (condition.type) {
    case 'equals': return val === filterText
    case 'notEqual': return val !== filterText
    case 'startsWith': return val.startsWith(filterText)
    case 'endsWith': return val.endsWith(filterText)
    case 'notContains': return !val.includes(filterText)
    case 'blank': return val === ''
    case 'notBlank': return val !== ''
    case 'contains':
    default: return val.includes(filterText)
  }
}

const AUTO_GROUP_COL_ID = 'ag-Grid-AutoColumn'

// Columns displaying the transaction number: the auto group column (grouped) and
// Trans # (flat). Both strip the PO prefix, so both compare the stripped value.
const TRANS_NUMBER_COL_IDS = new Set([AUTO_GROUP_COL_ID, 'trans_number'])

function getAutoGroupColumnValue (row) {
  if (row.__isGroup) return row.__groupLabel || ''
  if (row.trans_type === 'PO' && row.trans_number) {
    return String(row.trans_number).slice(2)
  }
  return row.trans_number || ''
}

/**
 * colId -> row field, for columns whose colId differs from the field they
 * actually display. Filtering must compare against the DISPLAYED value, not
 * the field that happens to share the column's id.
 * e.g. the Type column keeps colId 'trans_type' for saved-layout/state
 * compatibility but renders `source_code` (PA-2169).
 */
const FILTER_FIELD_BY_COL_ID = {
  trans_type: 'source_code'
}

// The value a column DISPLAYS for a leaf row — shared by filtering and sorting so
// a column can never filter on one value and sort on another.
function getRowValueForColumn (row, colId) {
  if (TRANS_NUMBER_COL_IDS.has(colId)) return getAutoGroupColumnValue(row)
  const field = FILTER_FIELD_BY_COL_ID[colId] || colId
  return row[field]
}

/**
 * Checks a leaf row against every active column filter in filterModel.
 * filterModel shape: { colId: { filterType, type, filter } } — this is what
 * ag-grid sends for standard agTextColumnFilter floating filters.
 */
function rowMatchesFilterModel (row, filterModel) {
  if (!filterModel) return true
  for (const colId of Object.keys(filterModel)) {
    if (!textConditionMatches(getRowValueForColumn(row, colId), filterModel[colId])) return false
  }
  return true
}

// Numeric columns: the collator handles digit runs inside text, not signs or
// decimals, so comparing these as text would put '9' after '100'.
const NUMERIC_SORT_COL_IDS = new Set(['amount', 'amount_native', 'account_id'])

// Blanks sort last in both directions — descending should not open with empty cells.
const BLANK_LAST = 1

function compareSortValues (a, b, colId) {
  const aBlank = a === null || a === undefined || a === ''
  const bBlank = b === null || b === undefined || b === ''
  if (aBlank || bBlank) {
    if (aBlank && bBlank) return 0
    return aBlank ? BLANK_LAST : -BLANK_LAST
  }
  if (NUMERIC_SORT_COL_IDS.has(colId)) {
    const numA = parseFloat(a) || 0
    const numB = parseFloat(b) || 0
    return numA < numB ? -1 : (numA > numB ? 1 : 0)
  }
  return ORDER_COLLATOR.compare(String(a), String(b))
}

// Applies sortModel ([{ colId, sort }], in the user's click order). The `desc` flip
// covers the comparison only, never the blanks-last rule.
function compareRowsBySortModel (rowA, rowB, sortModel) {
  for (const entry of sortModel) {
    const valueA = getRowValueForColumn(rowA, entry.colId)
    const valueB = getRowValueForColumn(rowB, entry.colId)
    const aBlank = valueA === null || valueA === undefined || valueA === ''
    const bBlank = valueB === null || valueB === undefined || valueB === ''
    const result = compareSortValues(valueA, valueB, entry.colId)
    if (result === 0) continue
    if (aBlank || bBlank) return result
    return entry.sort === 'desc' ? -result : result
  }
  return 0
}

function sortLeafRows (rows, sortModel) {
  if (!sortModel || !sortModel.length || rows.length < 2) return rows
  // slice(): the tree's own arrays hold the report order an unsorted grid relies on.
  return rows.slice().sort((rowA, rowB) => compareRowsBySortModel(rowA, rowB, sortModel))
}

/**
 * Does this node (or anything beneath it) contain at least one leaf row
 * matching the active filter? Used to hide group rows (accounts, GRAND
 * TOTAL) that have zero matching descendants once filtered — otherwise
 * you'd see empty account headers for accounts with no matching
 * transactions, which looks broken.
 */
function subtreeMatchesFilter (node, filterModel) {
  if (!filterModel || Object.keys(filterModel).length === 0) return true
  for (const row of node.rows) {
    if (rowMatchesFilterModel(row, filterModel)) return true
  }
  for (const child of node.children.values()) {
    if (subtreeMatchesFilter(child, filterModel)) return true
  }
  return false
}

/**
 * Sum of only the matching leaf rows beneath this node — so a group row's
 * displayed total reflects what's actually visible under the active
 * filter, not the unfiltered grand total.
 */
function subtreeFilteredAmount (node, filterModel) {
  if (!filterModel || Object.keys(filterModel).length === 0) return node.amount
  let total = 0
  for (const row of node.rows) {
    if (rowMatchesFilterModel(row, filterModel)) total += parseFloat(row.amount) || 0
  }
  for (const child of node.children.values()) {
    total += subtreeFilteredAmount(child, filterModel)
  }
  return total
}

/**
 * Walks the tree to the node addressed by ag-grid's groupKeys path.
 * groupKeys is the array of __groupKey values the user has drilled into,
 * e.g. ['GRAND TOTAL', '0401'].
 */
export function getNodeAtPath (root, groupKeys) {
  if (!groupKeys) return
  let node = root
  for (const key of groupKeys) {
    node = node.children.get(key)
    if (!node) return null
  }
  return node
}

/**
 * Builds the rows ag-grid should see at one level, for CLASSIC SSRM row
 * grouping (v25-safe) — NOT tree data.
 *
 * In this mode, ag-grid decides whether a level is a "group" level purely
 * from request depth (groupKeys.length vs number of row-group columns), not
 * from any flag on the data. So the rows we return for an intermediate
 * level must carry the value of the grouping FIELD for that depth — e.g.
 * `reportGroup: 'GRAND TOTAL'` at depth 0, `groupAccount: '0401'` at depth 1
 * — matching the two hidden, rowGroup:true columns added to columnDefs
 * (see Columns.js.CHANGES.md).
 *
 * A child is only rendered as a synthetic group row if it actually has
 * further children (real sub-structure below it). A child with no children
 * of its own is a true leaf (an individual transaction row, keyed by its
 * unique `code`) and is emitted as the real row data, unwrapped.
 *
 * IMPORTANT: group rows intentionally carry ONLY the fields needed for
 * classification (__isGroup/__groupKey/__groupLabel/reportGroup/groupAccount)
 * plus the rolled-up `amount` — nothing else. Every other column's own
 * valueGetter already returns '' when the relevant field is missing, so
 * this renders as a blank summary band (matching the original tree-data
 * behavior) rather than a row that looks like a duplicated transaction.
 * Do NOT spread a representative leaf row's fields onto a group row — that
 * bleeds vendor/GL description/doc#/dates etc. onto the group row and makes
 * it look like a second copy of a real transaction.
 *
 * @param {Object} node - current tree node (result of getNodeAtPath)
 * @param {number} depth - groupKeys.length for the current request;
 *   0 = building the GRAND TOTAL level, 1 = building the account level.
 * @param {Object|null} filterModel - ag-grid's active column filters, or
 *   null/empty if no filter is applied.
 *
 * No sortModel — grouped mode is not sortable; the order is buildLocalTree's.
 */
function buildRowsForNode (node, depth, filterModel) {
  const groupNodes = []
  const leafRows = []
  const hasFilter = filterModel && Object.keys(filterModel).length > 0

  for (const child of node.children.values()) {
    // Skip this branch entirely if filtering is active and nothing beneath
    // it matches — otherwise you'd see an account header with zero rows
    // under it once filtered.
    if (hasFilter && !subtreeMatchesFilter(child, filterModel)) continue

    if (child.children.size > 0) {
      groupNodes.push(child)
    } else {
      // True leaf: emit the real transaction row(s), filtered if a filter
      // is active.
      for (const row of child.rows) {
        if (!hasFilter || rowMatchesFilterModel(row, filterModel)) leafRows.push(row)
      }
    }
  }

  // Rows attached directly at this node (shouldn't normally happen given
  // this data's path structure, but kept for safety/generality).
  for (const row of node.rows) {
    if (!hasFilter || rowMatchesFilterModel(row, filterModel)) leafRows.push(row)
  }

  const outputRows = groupNodes.map((child) => {
    const groupRow = {
      __isGroup: true,
      __groupKey: child.key,
      __groupLabel: child.key,
      // Show the FILTERED total when a filter is active, so the group's
      // displayed amount matches what's actually visible underneath it —
      // not the unfiltered grand total.
      amount: hasFilter ? subtreeFilteredAmount(child, filterModel) : child.amount,
      // Safe defaults, cheap insurance in case any other column/formatter
      // ever reads these directly instead of going through __isGroup —
      // isEditable() already guards on __isGroup before touching either.
      line_desc: '',
      trans_type: '',
      source_code: ''
    }

    // Tag with the value of whichever grouping FIELD this depth corresponds
    // to — this is what ag-grid's rowGroupCols/groupKeys matching relies on,
    // in place of isServerSideGroup/getServerSideGroupKey.
    if (depth === 0) {
      groupRow.reportGroup = child.key // 'GRAND TOTAL'
    } else if (depth === 1) {
      groupRow.reportGroup = 'GRAND TOTAL'
      groupRow.groupAccount = child.key // account code
    }

    return groupRow
  })

  // Group bands first, then loose rows: ag-grid renders exactly the order
  // returned, and a leaf interleaved between account bands reads as broken.
  return [...outputRows, ...leafRows]
}

// Every leaf row, filtered, for flat mode. Walks in tree order so an unsorted flat
// grid still opens in the report's natural account-then-trans# order.
function collectLeafRows (node, filterModel, output = []) {
  const hasFilter = filterModel && Object.keys(filterModel).length > 0
  for (const row of node.rows) {
    if (!hasFilter || rowMatchesFilterModel(row, filterModel)) output.push(row)
  }
  for (const child of node.children.values()) {
    collectLeafRows(child, filterModel, output)
  }
  return output
}

// Signature of everything that changes which rows a request resolves to.
// Used to invalidate the block cache below.
function requestSignature (filterModel, sortModel) {
  return JSON.stringify(filterModel || null) + '|' + JSON.stringify(sortModel || null)
}
// Every leaf row, filtered, for flat mode. Walks in tree order so an unsorted flat
// grid still opens in the report's natural account-then-trans# order.
export function collectSelectedLeafRows (node, code, output = []) {
  // const hasFilter = filterModel && Object.keys(filterModel).length > 0
  for (const row of node.rows) {
    // console.log(row, "row")
    // if (!hasFilter || rowMatchesFilterModel(row, filterModel))
    if (row.account === code) output.push(row)
  }
  for (const child of node.children.values()) {
    collectSelectedLeafRows(child, code, output)
  }
  return output
}

/**
 * Factory: returns an ag-grid serverSideDatasource object wired to a given
 * tree. Call buildLocalTree() again and re-call this whenever the underlying
 * report data changes (refresh/rebuild), then reassign
 * gridOptions.serverSideDatasource — do NOT try to mutate the tree captured
 * in an old closure.
 *
 * @param {Object} tree - result of buildLocalTree()
 * @param {Object} [options]
 * @param {boolean} [options.flat] - "Group by Account" off: no group bands, every
 *   leaf row served at the root level and sorted. Requires the caller to have
 *   dropped the row-group columns, or ag-grid still asks for group levels.
 */
export function createLocalTreeDatasource (tree, options = {}) {
  const isFlat = !!options.flat
  // Blocks arrive one at a time, so cache per filter+sort signature — otherwise the
  // whole dataset is re-filtered and re-sorted on every scroll.
  let cacheSignature = null
  let cachedRowsByPath = new Map()

  return {
    getRows (params) {
      try {
        const { groupKeys, startRow, endRow, filterModel, sortModel } = params.request
        const signature = requestSignature(filterModel, sortModel)
        if (signature !== cacheSignature) {
          cacheSignature = signature
          cachedRowsByPath = new Map()
        }

        const cacheKey = isFlat ? '__flat__' : groupKeys.join(' ')
        let allRows = cachedRowsByPath.get(cacheKey)

        if (!allRows) {
          if (isFlat) {
            allRows = sortLeafRows(collectLeafRows(tree, filterModel), sortModel)
          } else {
            const node = getNodeAtPath(tree, groupKeys)
            if (!node) {
              params.success({ rowData: [], rowCount: 0 })
              return
            }
            // sortModel intentionally not passed — grouped mode is not sortable.
            allRows = buildRowsForNode(node, groupKeys.length, filterModel)
          }
          cachedRowsByPath.set(cacheKey, allRows)
        }

        params.success({
          rowData: allRows.slice(startRow, endRow),
          rowCount: allRows.length
        })
      } catch (e) {
        console.error('localTreeDatasource.getRows failed:', e)
        params.fail()
      }
    }
  }
}

/**
 * Convenience: total for the whole report (root.amount), used to replace
 * the old rootNode.aggData reads in pinTotalRow/pinSummaryTotalRow.
 */
export function getTreeTotal (tree) {
  return tree ? tree.amount : 0
}

export function getDisplayRowCountForNode (node) {
  let count = node && node.rows && node.rows.length ? node.rows.length : 0
  for (const child of node.children.values()) {
    count += child.children.size > 0 ? 1 : child.rows.length
  }
  return count
}

// Total of only the rows visible under `filterModel`. Flat mode has no GRAND TOTAL
// band, so the grid pins this as a bottom row and it must follow the filter.
export function getFilteredTreeTotal (tree, filterModel) {
  if (!tree) return 0
  return subtreeFilteredAmount(tree, filterModel)
}



// WEBPACK FOOTER //
// ./src/Mixins/SigmaClientNew/GeneralLedgerWorksheet/Localtreedatasource.js